import {
  type AlertProps,
  Alert as MuiAlert,
  Snackbar,
  type SnackbarContentProps,
} from '@mui/material';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import isClient from '~/scripts/isClient';

type SnackbarSeverity = 'success' | 'info' | 'warning' | 'error';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
  autoHideDuration?: number;
  action?: SnackbarContentProps['action'];
}

interface SnackbarContextProps {
  openSnackbar: (
    message: string,
    severity?: SnackbarSeverity,
    autoHideDuration?: number,
    action?: SnackbarContentProps['action']
  ) => void;
  closeSnackbar: () => void;
}

// 1) Create the Context
const SnackbarContext = createContext<SnackbarContextProps>({
  openSnackbar: () => {},
  closeSnackbar: () => {},
});

// For convenience, create an Alert component for the Snackbar content
const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function (props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  }
);

interface SnackbarProviderProps {
  children: ReactNode;
}

// 2) Create the Provider
export const SnackbarProvider = ({ children }: SnackbarProviderProps) => {
  const [snackbarState, setSnackbarState] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'info',
    autoHideDuration: 6000,
    action: undefined,
  });

  const openSnackbar = useCallback(
    (
      message: string,
      severity: SnackbarSeverity = 'info',
      autoHideDuration: number = 6000,
      action: SnackbarContentProps['action'] = undefined
    ) => {
      setSnackbarState({
        open: true,
        message,
        severity,
        autoHideDuration,
        action,
      });
      if (isClient() && typeof window.FS === 'function') {
        const track = () => {
          window.FS('trackEvent', {
            name: 'snackbar',
            properties: {
              message,
              severity,
            },
          });
        };
        if (typeof requestIdleCallback === 'function') {
          requestIdleCallback(track);
        } else {
          track();
        }
      }
    },
    []
  );

  const closeSnackbar = useCallback(() => {
    setSnackbarState((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  const handleClose = useCallback(
    (event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') {
        return;
      }
      closeSnackbar();
    },
    [closeSnackbar]
  );

  const contextValue = useMemo(
    () => ({ openSnackbar, closeSnackbar }),
    [openSnackbar, closeSnackbar]
  );

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={snackbarState.open}
        autoHideDuration={snackbarState.autoHideDuration || undefined}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        action={snackbarState.action}
        sx={{ marginTop: '50px' }}>
        <Alert onClose={handleClose} severity={snackbarState.severity}>
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

// 3) Export a custom hook for easy consumption
/**
 *
 * openSnackbar and closeSnackbar
 */
export const useSnackbar = () => {
  return useContext(SnackbarContext);
};
